import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '@shared/api/http'
import { AppError } from '@shared/api/error'
import type { DashboardPeriod } from '../lib/admin-dashboard-period'
import type { AdminDashboard, AdminDashboardParams, AdminSalesSeries } from './admin.api'

export function useAdminDashboardOverview(
  params: AdminDashboardParams,
  period: DashboardPeriod,
  live: boolean,
  options?: { enabled?: boolean; refetchOnWindowFocus?: boolean },
) {
  return useQuery({
    queryKey: ['admin-dashboard-overview', params, period],
    queryFn: async ({ signal }) => {
      const { dateFrom, dateTo, ...shared } = params
      const filters = {
        ...shared,
        period,
        ...(period === 'custom' ? { dateFrom, dateTo } : {}),
      }
      const search = new URLSearchParams()
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined) search.set(key, String(value))
      }
      const [dashboard, series] = await Promise.all([
        apiFetch<AdminDashboard>(`/admin/dashboard?${search}`, { signal }),
        apiFetch<AdminSalesSeries>(`/admin/dashboard/sales-series?${search}`, { signal }),
      ])
      const range =
        period === 'all'
          ? {}
          : series.from && series.to
            ? { dateFrom: series.from, dateTo: series.to }
            : { dateFrom: filters.dateFrom, dateTo: filters.dateTo }
      return { dashboard, series, range }
    },
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
    refetchInterval: (query) =>
      live && period === 'today' && query.state.status !== 'error' ? 30_000 : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus:
      options?.refetchOnWindowFocus ??
      ((query) => live && period === 'today' && query.state.status !== 'error'),
    retry: (failureCount, error) =>
      failureCount < 1 && !(error instanceof AppError && error.status >= 500),
  })
}
